import { getProjectIdsFromUrl } from './getProjectIdsFromUrl';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { CONTENT_TYPE, HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { ErrorCode } from '@dealstack/shared/lib/common/errors';
import {
	API_FETCH_ABORT_SYMBOL,
	API_FORBIDDEN_EXCEPTION_ERROR,
	API_LOCAL_ERROR,
} from '@dealstack/frontend/shared/other/react/ApiWrapper/symbols';
import {
	ApiWrapperError,
	ServiceRequestOptions,
} from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import qs from 'qs';

import { IRequestParams } from './domain';
import { getGlobalConfig, GlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { apiClient, REQUEST_ID_HEADER } from '@dealstack/frontend/api/client';
import { uuidv7 } from 'uuidv7';
import { currentReturnTarget, redirectToLoginPage } from '@dealstack/frontend/shared/redirecting';
import {
	readSessionDeadlineFromHeaders,
	readSessionEndReasonFromHeaders,
} from '@dealstack/frontend/shared/mechanism/session/sessionDeadline';
import { isUserPresumedIdle } from '@dealstack/frontend/shared/mechanism/session/sessionActivity';
import { isSigningOutIntentionally } from '@dealstack/frontend/shared/mechanism/session/intentionalSignOut';
import {
	BACKGROUND_REQUEST_HEADER,
	BACKGROUND_REQUEST_HEADER_VALUE,
	SessionEndReason,
} from '@dealstack/shared/lib/common/session/sessionContract';
import { RETRY_AFTER_HEADERS } from '@dealstack/frontend/shared/dataExchange/services/rateLimitError';
import {
	reportTransportError,
	toEndpointShape,
	wasReportedToSentry,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/transportError';

export enum API_LAYER_LOCAL_STORAGE_KEYS {
	SANITY_PREVIEW_TOKEN = 'SANITY_PREVIEW_TOKEN',
}

export const makeRequest = async <Result>(
	params: IRequestParams,
	options?: ServiceRequestOptions
): Promise<Result> => {
	try {
		const abortController = options?.abortController ?? undefined;
		const abortSignal = options?.abortSignal ?? undefined;

		const globalConfig = getGlobalConfig();

		const headers = await createHeaders(params);
		const url = createUrl(params, globalConfig);

		const response = await sendRequest({
			params,
			headers,
			url,
			abortController,
			abortSignal,
		});
		readSessionDeadlineFromHeaders(response.headers);
		const parsedResponse = await parseResponse<Result>(response);
		return parsedResponse;
	} catch (error) {
		if (options?.skipErrorHandler) {
			throw error;
		}
		const errorInstance = await createErrorInstance(error, options, params);
		throw errorInstance;
	}
};

export const createErrorInstance = async (
	error: unknown,
	options?: ServiceRequestOptions,
	params?: Pick<IRequestParams, 'method' | 'endpoint'>
) => {
	const wrappedError = new ApiWrapperError<ErrorCode>(API_LOCAL_ERROR);
	if (params) {
		wrappedError.request = {
			method: params.method,
			endpoint: toEndpointShape(params.endpoint),
		};
	}
	if (error instanceof Response) {
		wrappedError.status = error.status;
	} else {
		// Keeps the original throwable reachable, so callers can tell a fetch TypeError
		// (offline) apart from a defect raised inside the API layer, and so Sentry can
		// chain the two. Non-enumerable: it must not land in `{...err}` payloads, and a
		// Response is deliberately not carried — it is large and already consumed.
		Object.defineProperty(wrappedError, 'cause', {
			value: error,
			enumerable: false,
			writable: true,
			configurable: true,
		});
		wrappedError.alreadyReported = wasReportedToSentry(error);
	}
	if (error instanceof Response && error.status === 403) {
		wrappedError.symbol = API_FORBIDDEN_EXCEPTION_ERROR;
	}

	if (error instanceof Response && error.status === 429) {
		const [retryAfter] = RETRY_AFTER_HEADERS.map((header) => error.headers.get(header)).filter(
			(value): value is string => typeof value === 'string'
		);
		const seconds = retryAfter === undefined ? NaN : Number(retryAfter);
		if (Number.isFinite(seconds) && seconds >= 0) {
			wrappedError.retryAfterMs = seconds * 1000;
		}
	}

	if (error instanceof Response && error.status === 401) {
		// Recorded whether or not we redirect, so a caller that suppresses the redirect
		// can still explain the right thing instead of guessing.
		const sessionEndReason = readSessionEndReasonFromHeaders(error.headers);
		if (sessionEndReason) {
			wrappedError.sessionEndReason = sessionEndReason;
		}
		// A request losing the race with a deliberate logout must not redirect: logout
		// revokes the session, so this 401 reports REVOKED, and telling the user their
		// session ended unexpectedly would be false. The logout path redirects itself.
		if (!options?.skipLoginRedirect && !isSigningOutIntentionally()) {
			redirectToLoginPage(currentReturnTarget(), sessionEndReason);
		}
	}

	if ((error as Record<string, string>)?.name === 'AbortError') {
		wrappedError.symbol = API_FETCH_ABORT_SYMBOL;
	}
	const parsedError = await isKnownBackendError(error);
	if (parsedError) {
		wrappedError.error = parsedError;
	}
	return wrappedError;
};

// Re-exported from its new home beside `ApiWrapperError`; existing callers import it from here.
export { isStandardBackendError } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';

/**
 * Extract a human-readable `error.error.message` from an unknown thrown value,
 * narrowing with `in`/`typeof` guards rather than a shape cast. Returns undefined
 * when the value doesn't carry a non-empty message.
 */
export const getBackendErrorMessage = (error: unknown): string | undefined => {
	if (typeof error !== 'object' || error === null || !('error' in error)) {
		return undefined;
	}
	const inner = error.error;
	if (typeof inner !== 'object' || inner === null || !('message' in inner)) {
		return undefined;
	}
	const message = inner.message;
	return typeof message === 'string' && message.length > 0 ? message : undefined;
};

/**
 * The generated client's thrown body with the response's HTTP status beside it as `statusCode`. The backend's
 * `{ error: { message } }` carries no status, so without it a caller cannot tell a 409 from a 422.
 */
export const withHttpStatus = (error: unknown, status: number | undefined): unknown =>
	status !== undefined &&
	typeof error === 'object' &&
	error !== null &&
	!Array.isArray(error) &&
	!('statusCode' in error)
		? { ...error, statusCode: status }
		: error;

/** The HTTP status a thrown value carries: the generated client's `statusCode`, or the legacy wrapper's `status`. */
export const httpStatusOf = (error: unknown): number | undefined => {
	if (error instanceof ApiWrapperError) {
		return error.status;
	}
	return typeof error === 'object' &&
		error !== null &&
		'statusCode' in error &&
		typeof error.statusCode === 'number'
		? error.statusCode
		: undefined;
};

/** A mutation's `meta` naming the statuses its caller shows itself, so the global error toast stays out. */
export const CALLER_HANDLES_CONFLICT = { handledStatuses: [409] };

/** A failed request whose effect is not known: no HTTP answer (a dropped connection), or a 5xx. */
export const isOutcomeUnknown = (error: unknown): boolean => {
	const status = httpStatusOf(error);
	return status === undefined || status >= 500;
};

/**
 * Whether the failed mutation's `meta` names the error as one its caller shows itself: its status in
 * `handledStatuses`, or, with `handlesUnknownOutcome`, a failure whose effect is not known.
 */
export const isHandledByCaller = (
	error: unknown,
	meta: Record<string, unknown> | undefined
): boolean => {
	if (meta?.handlesUnknownOutcome === true && isOutcomeUnknown(error)) {
		return true;
	}
	const handled = meta?.handledStatuses;
	const status = httpStatusOf(error);
	return Array.isArray(handled) && status !== undefined && handled.includes(status);
};

/**
 * The message to show a user for an unknown thrown value, or undefined when the value
 * carries nothing worth showing — in which case the caller falls back to its own
 * translated string.
 *
 * An `ApiWrapperError` always yields undefined. Its own `message` describes the
 * transport failure for Sentry ("API request failed (500)"), and the backend's message
 * is untranslated English that routinely embeds ids ("No pending eID Easy session for
 * docId: …"), so neither belongs in a toast. Callers that deliberately surface the
 * backend's wording use `getApiErrorMessage`.
 */
export const getUserFacingErrorMessage = (error: unknown): string | undefined => {
	if (error instanceof ApiWrapperError) {
		return undefined;
	}
	return error instanceof Error && error.message ? error.message : undefined;
};

/**
 * As `getUserFacingErrorMessage`, but falls back to the backend's own message. Only for
 * the surfaces that already showed it — e.g. claim-signer — where the
 * server's wording is more useful than a generic string.
 */
export const getApiErrorMessage = (error: unknown): string | undefined =>
	getUserFacingErrorMessage(error) ?? getBackendErrorMessage(error);

export const isForbiddenApiError = (error: unknown): boolean =>
	error instanceof ApiWrapperError && error.symbol === API_FORBIDDEN_EXCEPTION_ERROR;

/** True only for a definitive 401 Unauthorized, not for transient/network failures. */
export const isUnauthorizedApiError = (error: unknown): boolean =>
	error instanceof ApiWrapperError && error.status === 401;

/** The server's own account of why a session ended, when the failure carried one. */
export const getSessionEndReason = (error: unknown): SessionEndReason | null =>
	error instanceof ApiWrapperError ? error.sessionEndReason ?? null : null;

const isKnownBackendError = async (error: unknown) => {
	try {
		if (!(error instanceof Response)) {
			return;
		}
		const parsedResponseError: { error: ErrorCode } = await error.json();
		return parsedResponseError.error;
	} catch {
		logger.warn('Failed to parse Response to JSON');
	}
	return undefined;
};

export const createHeaders = async (params: Parameters<typeof makeRequest>[0]) => {
	const { dealId, mipId } = getProjectIdsFromUrl();
	const sanityPreviewToken = sessionStorage.getItem(
		API_LAYER_LOCAL_STORAGE_KEYS.SANITY_PREVIEW_TOKEN
	);

	const result: Record<string, string> = {
		'Content-Type': CONTENT_TYPE.JSON,
		[REQUEST_ID_HEADER]: uuidv7(),
	};
	// Declared so polling cannot keep a forgotten tab's session alive. Callers that are
	// always background set the header explicitly via customHeaders.
	if (isUserPresumedIdle()) {
		result[BACKGROUND_REQUEST_HEADER] = BACKGROUND_REQUEST_HEADER_VALUE;
	}
	if (dealId) {
		result['deal-id'] = dealId;
	}
	if (mipId) {
		result['mip-id'] = mipId;
	}
	// customHeaders can override URL-derived headers if needed. An explicit null REMOVES the
	// URL-derived one rather than overriding it, which is how a request that has its own target
	// opts out of the URL entirely — a debounced save fires after navigation has moved on, and
	// a target with no programme must not inherit the programme the URL now shows.
	for (const [name, value] of Object.entries(params.customHeaders ?? {})) {
		if (value === null || value === undefined) {
			delete result[name];
			continue;
		}

		result[name] = String(value);
	}
	if (sanityPreviewToken) {
		result['x-sanity-preview-token'] = sanityPreviewToken;
	}
	return result;
};

export const createUrl = (
	params: Parameters<typeof makeRequest>[0],
	config: GlobalConfig
): string => {
	const { endpoint, base, queryString } = params;

	// Determine the base URL, defaulting to config.apiUrl
	const baseUrl = base || config.apiUrl;

	// Generate query string if provided
	const queryStringPart = queryString
		? qs.stringify(queryString, {
				addQueryPrefix: true,
				skipNulls: true,
			})
		: '';

	// Concatenate base, endpoint, and query string
	return `${baseUrl}${endpoint || ''}${queryStringPart}`;
};

interface ICreateRequestInterface {
	params: Parameters<typeof makeRequest>[0];
	headers: Awaited<ReturnType<typeof createHeaders>>;
	url: ReturnType<typeof createUrl>;
	abortController?: AbortController | null;
	abortSignal?: AbortSignal;
}

const createRequest = ({
	params,
	headers,
	url,
	abortController,
	abortSignal,
}: ICreateRequestInterface) => {
	const { method, body } = params;

	return apiClient(url, {
		method,
		headers,
		body: body ? body : null,
		mode: 'cors',
		redirect: 'follow',
		signal: abortSignal ?? abortController?.signal,
	});
};

/** Issues the request, reporting transport-level failures before rethrowing so
 *  the caller's existing error handling is unchanged. */
const sendRequest = async (args: ICreateRequestInterface): Promise<Response> => {
	const startedAt = performance.now();
	try {
		return await createRequest(args);
	} catch (error) {
		reportTransportError(error, {
			method: args.params.method,
			url: args.url,
			requestId: args.headers[REQUEST_ID_HEADER],
			durationMs: performance.now() - startedAt,
		});
		throw error;
	}
};

export const parseResponse = async <T>(response: Response): Promise<T> => {
	if (!response.ok || response.status >= 400) {
		throw response;
	}

	if (response.status === 204) {
		return undefined as T;
	}

	const contentType = response.headers.get('Content-type');
	if (contentType?.includes(CONTENT_TYPE.JSON)) {
		return await response.json();
	}
	if (contentType?.includes(CONTENT_TYPE.TEXT)) {
		return (await response.text()) as unknown as Promise<T>;
	}
	if (contentType?.includes(CONTENT_TYPE.EXCEL)) {
		return (await response.blob()) as unknown as Promise<T>;
	}
	if (contentType?.includes(CONTENT_TYPE.PDF)) {
		return (await response.blob()) as unknown as Promise<T>;
	}
	if (contentType?.includes(CONTENT_TYPE.ZIP)) {
		return (await response.blob()) as unknown as Promise<T>;
	}
	throw new Error('Couldnt parse content');
};

/** Narrows the parsed `error` field of a failed upload to the backend's error shape. */
const isBackendErrorCode = (value: unknown): value is ErrorCode =>
	typeof value === 'object' && value !== null && 'code' in value;

export const uploadFileRequest = async <Result>(
	params: IRequestParams,
	body: FormData,
	onUploadProgress?: (percent: number) => void,
	_options?: ServiceRequestOptions
): Promise<Result> => {
	const globalConfig = getGlobalConfig();

	const headers = await createHeaders(params);
	const url = createUrl(params, globalConfig);
	delete headers['Content-Type'];

	// Calculate total size of all files
	const totalSize = Array.from(body.entries()).reduce(
		(acc, [, value]) => acc + (value instanceof File ? value.size : 0),
		0
	);

	let loadedSize = 0;

	const uploadChunk = async (chunk: Blob) => {
		const chunkFormData = new FormData();
		chunkFormData.append('file', chunk);
	};

	const uploadWithProgress = async () => {
		for (const [key, value] of body.entries()) {
			if (value instanceof File) {
				const chunkSize = 1024 * 1024; // 1MB chunks
				for (let start = 0; start < value.size; start += chunkSize) {
					const chunk = value.slice(start, start + chunkSize);
					await uploadChunk(chunk);
					loadedSize += chunk.size;
					if (onUploadProgress) {
						const progressPercent = Math.round((loadedSize / totalSize) * 100);
						onUploadProgress(progressPercent);
					}
				}
			} else {
				const chunkFormData = new FormData();
				chunkFormData.append(key, value);
			}
		}
	};

	await uploadWithProgress();

	const response = await sendRequest({
		params: { endpoint: params.endpoint, body, method: HTTP_METHOD.POST },
		headers,
		url,
	});

	if (!response.ok) {
		let errorPayload: unknown = response.statusText;
		try {
			const { error } = await response.json();
			errorPayload = error;
		} catch {
			// JSON parsing failed — keep statusText as fallback
		}

		// Routed through createErrorInstance like every other failure, so an upload gets
		// the same Error, status, fingerprint and 401 handling as the rest of the API.
		const wrapped = new ApiWrapperError<ErrorCode>(
			response.status === 403 ? API_FORBIDDEN_EXCEPTION_ERROR : API_LOCAL_ERROR
		);
		wrapped.status = response.status;
		wrapped.request = {
			method: params.method,
			endpoint: toEndpointShape(params.endpoint),
		};
		if (isBackendErrorCode(errorPayload)) {
			wrapped.error = errorPayload;
		}
		throw wrapped;
	}

	const parsedResponse = (await response.json()) as Result;
	return parsedResponse;
};

export const fetchFileRequest = async (
	endpoint: string,
	options?: ServiceRequestOptions,
	customHeaders?: Record<string, string>
) => {
	const globalConfig = getGlobalConfig();
	const params = { endpoint, method: HTTP_METHOD.GET, customHeaders };
	const headers = await createHeaders(params);
	const url = createUrl(params, globalConfig);

	try {
		const response = await sendRequest({
			params,
			headers,
			url,
			abortController: options?.abortController,
			abortSignal: options?.abortSignal,
		});
		if (!response.ok || response.status >= 400) {
			throw response;
		}
		if (!response.body) {
			throw new Error('Failed to fork stream');
		}
		const [stream, streamCopy] = response.body.tee();

		const reader = streamCopy.getReader();
		let done = false;
		while (!done) {
			const res = await reader.read();
			done = res.done;
			if (done) {
				const finalResponse = new Response(stream);
				for (const [key, value] of response.headers.entries()) {
					finalResponse.headers.set(key, value);
				}
				return finalResponse;
			}
		}
	} catch (error) {
		const errorInstance = await createErrorInstance(error, options, params);
		throw errorInstance;
	}
};
